import React, { createRef, useEffect, useState } from "react";
import { animated, useSpring } from "react-spring";
import { faAngleDown, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";

import Badge from "../Badge";
import Icon from "../Icon";
import { NavLink } from "react-router-dom";
import { auth } from "../../firebase/config";
import useDynamicNav from "../../hooks/useDynamicNav";
import useLiveSession from "../../hooks/useLiveSession";

export interface NavProps {
  isOpen: boolean;
  onClick: () => void;
}

const NavbarNav = ({ isOpen, onClick }: NavProps) => {
  const [currentDropdown, setCurrentDropdown] = useState("");
  const items = useDynamicNav();

  // Animations
  const animation = useSpring({
    opacity: isOpen ? "1" : "0",
    height: isOpen ? "100vh" : "0vh",
  });
  const dropdownAnimation = useSpring({
    opacity: currentDropdown !== "" ? "1" : "0",
  });

  // Close when click outside
  const ref = createRef<HTMLUListElement>();
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setCurrentDropdown("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref]);

  // Get live session
  const room1 = useLiveSession(1);
  const room2 = useLiveSession(2);
  const room3 = useLiveSession(3);

  // Handle Sign out button
  const handleSignOut = () => {
    auth
      .signOut()
      .then(() => {
        window.location.href = "/";
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <animated.nav
      className="relative"
      style={{
        ...animation,
        display: animation.opacity.interpolate((o) =>
          o === "0" ? "none" : ""
        ),
        height: window.innerWidth >= 1024 ? "" : animation.height,
      }}
    >
      <ul className="py-4 text-gray-400 lg:flex lg:justify-end" ref={ref}>
        {items.map((item, key) => (
          <li key={key}>
            {item.dropdown && item.dropdownID ? (
              <div className="relative">
                <button
                  type="button"
                  className="inline-block w-full p-4 my-1 text-xs font-bold text-left uppercase transition-colors duration-300 rounded hover:bg-gray-200 lg:w-auto lg:p-2 xl:mx-2"
                  onClick={() =>
                    setCurrentDropdown((current) =>
                      current === item.dropdownID ? "" : item.dropdownID || ""
                    )
                  }
                >
                  {item.icon && (
                    <>
                      <Icon icon={item.icon} />{" "}
                    </>
                  )}
                  {item.name}{" "}
                  {item.dropdown ? <Icon icon={faAngleDown} /> : null}
                </button>

                <div className="relative">
                  {room1 || room2 || room3 ? (
                    <div
                      className="absolute right-0"
                      style={{
                        top: "0",
                        transform: "translate(20px, -60px)",
                      }}
                    >
                      <Badge text="LIVE" />
                    </div>
                  ) : null}
                  <animated.ul
                    className="lg:flex lg:absolute lg:flex-wrap lg:bg-gray-100 lg:p-2 lg:rounded lg:w-48 lg:shadow-lg"
                    style={{
                      ...dropdownAnimation,
                      display: dropdownAnimation.opacity.interpolate((o) =>
                        o === "0"
                          ? "none"
                          : currentDropdown !== item.dropdownID
                          ? "none"
                          : "block"
                      ),
                    }}
                  >
                    {item.dropdown.map((dropdownItem, dropdownKey) => (
                      <li className="lg:w-full" key={dropdownKey}>
                        <NavLink
                          to={dropdownItem.link || ""}
                          className="inline-block w-full px-8 py-4 my-1 text-xs font-bold text-left uppercase transition-colors duration-300 rounded hover:bg-gray-200 lg:w-full lg:p-2"
                          activeClassName="bg-gray-200"
                          onClick={() => setCurrentDropdown("")}
                        >
                          {dropdownItem.name?.includes("LIVE") ? (
                            <div className="w-full">
                              {dropdownItem.name.replace(/LIVE/g, "")}{" "}
                              <span className="float-right font-bold text-red-600">
                                LIVE
                              </span>
                            </div>
                          ) : (
                            dropdownItem.name
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </animated.ul>
                </div>
              </div>
            ) : (
              <NavLink
                to={item.link || ""}
                className="inline-block w-full p-4 my-1 text-xs font-bold uppercase transition-colors duration-300 rounded hover:bg-gray-200 lg:w-auto lg:p-2 xl:mx-2"
                activeClassName="text-black bg-gray-200 lg:bg-transparent"
                exact={item.exact}
                onClick={onClick}
              >
                {item.icon && (
                  <>
                    <Icon icon={item.icon} />{" "}
                  </>
                )}
                {item.name}
              </NavLink>
            )}
          </li>
        ))}
        <li>
          <button
            type="button"
            className="inline-block w-full p-4 my-1 text-xs font-bold text-left uppercase transition-colors duration-300 rounded hover:bg-gray-200 lg:w-auto lg:p-2 xl:mx-2"
            onClick={() => handleSignOut()}
          >
            <Icon icon={faSignOutAlt} /> Sign Out
          </button>
        </li>
      </ul>
    </animated.nav>
  );
};

export default NavbarNav;
